// Extraído de Whatsapp.page.tsx pra ser compartilhado com o painel de
// grupos (components/whatsappGroups/) — são funções puras, mover não muda
// nenhum comportamento existente.

// Telefone é salvo cru (dígitos + DDI 55, ver utils/whatsappPhone.ts no
// backend) pra bater com o formato que a Z-API usa — só formata bonito na
// hora de exibir, nunca no que é salvo/comparado.
export function formatPhoneDisplay(phone: string): string {
  // Defensivo: dado antigo/malformado pode ter gravado phone como number —
  // sem isso, uma única linha ruim derruba a tela inteira (já aconteceu).
  const raw = String(phone ?? "");
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 12 && digits.length !== 13) return raw;
  const ddi = digits.slice(0, 2);
  const ddd = digits.slice(2, 4);
  const number = digits.slice(4);
  const numberFormatted =
    number.length === 9
      ? `${number.slice(0, 5)}-${number.slice(5)}`
      : `${number.slice(0, 4)}-${number.slice(4)}`;
  return `+${ddi} (${ddd}) ${numberFormatted}`;
}

// Celular brasileiro pode ter o 9º dígito ou não, dependendo de quando a
// conta foi registrada no WhatsApp — o telefone salvo no contato (digitado
// por humano, sempre com o 9 hoje em dia) pode divergir do JID real que o
// WhatsApp usa pra essa conta (às vezes sem o 9). Usado pra casar um
// participante de grupo (telefone vem do JID real, ver backend
// WhatsappGroupService.fetchRealParticipants) contra o contato salvo, que
// pode estar no outro formato.
export function brPhoneVariants(phone: string): string[] {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length === 13 && digits.slice(0, 2) === "55" && digits[4] === "9") {
    return [digits, digits.slice(0, 4) + digits.slice(5)];
  }
  if (digits.length === 12 && digits.slice(0, 2) === "55") {
    return [digits, digits.slice(0, 4) + "9" + digits.slice(4)];
  }
  return [digits];
}

// Bug real corrigido (2026-09-21): ManageParticipantsModal filtrava quem já
// é membro do grupo comparando o telefone do contato direto contra
// group.participants[].phone (JID real do WhatsApp) sem passar por
// brPhoneVariants — um contato já adicionado ao grupo continuava
// aparecendo como "disponível" e podia ser adicionado de novo, sempre que
// o JID divergisse do telefone salvo só pelo 9º dígito. Mesma raiz do
// problema que nameForPhone (GroupDetailPanel) já trata pra achar o nome
// do participante — faltava aplicar também no filtro de disponibilidade.
export function isContactAlreadyParticipant(
  contactPhone: string,
  participantPhones: string[],
): boolean {
  const variantSet = new Set(participantPhones.flatMap((p) => brPhoneVariants(p)));
  return variantSet.has(String(contactPhone ?? "").replace(/\D/g, ""));
}

// Hora pra bolha de mensagem (sempre HH:mm, é óbvio pelo contexto que é do
// dia da mensagem). Data completa só some quando o dia é hoje na listagem —
// ver formatListTime.
export function formatMessageTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatListTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  return isToday
    ? date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

// Usuários sentiram falta do separador de dia que o próprio WhatsApp mostra
// (pedido explícito, 2026-09-29) — sem ele, mensagem de ontem 17:30 e de
// hoje 08:30 ficavam grudadas na listagem, sem nenhuma pista visual de que
// virou o dia. `now` é parâmetro (não `new Date()` interno) só pra dar pra
// testar "Hoje"/"Ontem" de forma determinística.
export function formatDayDivider(timestamp: number, now: Date = new Date()): string {
  const date = new Date(timestamp);
  if (isSameCalendarDay(date, now)) return "Hoje";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameCalendarDay(date, yesterday)) return "Ontem";
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

// Decide se a mensagem `timestamp` precisa de um separador de dia acima
// dela — true quando é a primeira mensagem da lista ou quando o dia dela é
// diferente do dia da mensagem anterior.
export function shouldShowDayDivider(timestamp: number, previousTimestamp?: number): boolean {
  if (previousTimestamp === undefined) return true;
  return !isSameCalendarDay(new Date(timestamp), new Date(previousTimestamp));
}
